import React, { useEffect, useState } from 'react';
import './MyEngagement.scss';
import {
    LevelOfService,
    LevelsOfService,
} from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { LegalEntityTypeList } from '../../../slices/marketplace/timeline/models/enums/legal-enity-type';
import { stateToCodeMapper } from '../../../utils/locationsHelper';
import { Button } from 'reactstrap';
import { SuspendEngagementPopup } from './SuspendEngagementPopup/SuspendEngagementPopup';
import {
    EngagementProcessStatus,
    EngagementProcessStatuses,
} from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { TerminateEngagementPopup } from './TerminateEngagementPopup/TerminateEngagementPopup';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { ComingSoonCommercialTermsTable } from '../../PracticeBrokerageServices/ComingSoonCommercialTermsTable/ComingSoonCommercialTermsTable';
import { ComingSoonCommercialTermsCalculator } from '../../PracticeBrokerageServices/ComingSoonCommercialTermsTable/CommingSoonCommercialTermsCalculator';
import { BrokerageType } from '../../../slices/marketplace/pricing/models/brokerage-type.enum';
import { ComingSoonQuote } from '../../PracticeBrokerageServices/RequestPreOrderEngagementAgreementModal/Models/Quote';
import EditEngagementValuesModal from './EditEngagementValuesModal/EditEngagementValuesModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

interface MyEngagementProps {
    fetchData(): void;
    isAdminView: boolean;
}

const MyEngagement = (props: MyEngagementProps) => {
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const [
        isSuspendEngagementPopupVisible,
        setIsSuspendEngagementPopupVisible,
    ] = useState(false);
    const [
        isTerminateEngagementPopupVisible,
        setIsTerminateEngagementPopupVisible,
    ] = useState(false);
    const [isResumeEngagementPopupVisible, setIsResumeEngagementPopupVisible] =
        useState(false);
    const [isResumeEngagementInProcess, setIsResumeEngagementInProcess] =
        useState(false);
    const [isEditValuesModalVisible, setIsEditValuesModalVisible] = useState(false);
    const [isSavingValues, setIsSavingValues] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const [notificationFlagCode, setNotificationFlagCode] = useState<string>();
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const [quotes, setQuotes] = useState<ComingSoonQuote>();

    const formattedDate = new Date(
        currentEngagement.engagementProcess.startDate
    ).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
    });

    const party = useCurrentMarketplaceParty(currentEngagement?.id);
    const isPartyBroker = party === TimelineParty.Broker;

    useEffect(() => {
        const notificationCode = `${NotificationFlagsCategories.MpCode}.${currentEngagement.engagementProcess.timelineId}.${NotificationFlagsCategories.EngagementCode}.${NotificationFlagsCategories.EngagementStatusCode}`;
        setNotificationFlagCode(notificationCode);
    }, [currentEngagement.engagementProcess]);

    useEffect(() => {
        calculateEngagementCommercialTerms();
    }, [currentEngagement.levelOfService, marketplacePricing]);

    const columns = [
        {
            dataField: 'practiceName',
            text: 'Practice Name',
            sort: false,
            headerClasses: 'practice-name-header',
        },
        {
            dataField: 'entityName',
            text: 'Legal Entity Name',
            sort: false,
            headerClasses: 'practice-name-header',
        },
        {
            dataField: 'entityType',
            headerClasses: 'entity-type-header',
            text: 'Legal Entity Type',
            sort: false,
            formatter: (cellContent: number) => {
                return (
                    LegalEntityTypeList.find((item) => item.id === cellContent)
                        ?.abbreviation ?? 'NA'
                );
            },
        },
        {
            dataField: 'stateOfRegistration',
            headerClasses: 'state-of-registration-header',
            text: 'Legal Entity State of Registration',
            sort: false,
            formatter: (cellContent: string) => {
                return stateToCodeMapper(cellContent) ?? 'NA';
            },
        },
    ];

    const getPracticesTable = () => {
        return (
            <BootstrapTable
                bootstrap4
                remote
                keyField="id"
                data={currentEngagement.engagementProcess.practices}
                columns={columns}
                noDataIndication="No data available"
            />
        );
    };

    const resumeEngagement = () => {
        setIsResumeEngagementInProcess(true);
        axios
            .post(
                `/api/Marketplace/Engagements/${currentEngagement.engagementProcess.timelineId}/Resume`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An error occured during resuming engagement',
                        'error'
                    );
                } else {
                    setIsResumeEngagementPopupVisible(false);
                    props.fetchData();
                }
            })
            .catch(() => {
                createNotification(
                    'An error occured during resuming engagement',
                    'error'
                );
            })
            .finally(() => {
                setIsResumeEngagementInProcess(false);
                props.fetchData();
            });
    };

    const handleSaveEngagementValues = (fteDvm: number, ltmRevenue: number, reason: string) => {
        setIsSavingValues(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${currentEngagement.engagementProcess.timelineId}/startFteDvmAndLtmRevenue`,
                {
                    startFteDvm: fteDvm,
                    startLtmRevenue: ltmRevenue,
                    reason: reason,
                }
            )
            .then((response) => {
                if (response?.status === 200) {
                    createNotification(
                        'Engagement values updated successfully',
                        'success'
                    );
                    setIsEditValuesModalVisible(false);
                    props.fetchData();
                } else {
                    createNotification(
                        'An error occurred while updating engagement values',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An error occurred while updating engagement values',
                    'error'
                );
            })
            .finally(() => {
                setIsSavingValues(false);
            });
    };

    const getButtonsSection = () => {
        return (
            <div className="buttons-section">
                {currentEngagement.engagementProcess.status ===
                    EngagementProcessStatus.Suspended &&
                !props.isAdminView &&
                !isPartyBroker ? (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => {
                            setIsResumeEngagementPopupVisible(true);
                        }}>
                        Resume Engagement
                    </Button>
                ) : currentEngagement.engagementProcess.status ===
                      EngagementProcessStatus.Current &&
                  !props.isAdminView &&
                  !isPartyBroker ? (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => {
                            setIsSuspendEngagementPopupVisible(true);
                        }}>
                        Suspend Engagement
                    </Button>
                ) : (
                    <></>
                )}
                {currentEngagement.engagementProcess.status !==
                    EngagementProcessStatus.Terminated &&
                    !props.isAdminView &&
                    !isPartyBroker && (
                        <Button
                            color="primary"
                            className="select-documents-btn"
                            onClick={() => {
                                setIsTerminateEngagementPopupVisible(true);
                            }}>
                            Terminate Engagement
                        </Button>
                    )}
            </div>
        );
    };

    const calculateEngagementCommercialTerms = () => {
        if (
            currentEngagement.levelOfService ===
            LevelOfService.StrategicFinancialAdvisoryService
        ) {
            return;
        }

        const fte = currentEngagement.engagementProcess.startFteDvm || 0;
        const ltmRevenue = currentEngagement.engagementProcess.startLtmRevenue || 0;
        const brokerageType =
            currentEngagement.levelOfService ===
            LevelOfService.FullServiceBrokerage
                ? BrokerageType.FullService
                : BrokerageType.Discount;

        let quo = {} as ComingSoonQuote;
        quo.leftText =
            ComingSoonCommercialTermsCalculator.calculateCommercialTerms(
                fte,
                ltmRevenue,
                brokerageType,
                marketplacePricing
            );

        setQuotes(quo);
    };

    const getCommercialTermsSection = () => {
        return (
            <div className="commercial-terms-section">
                <h6>My Engagement Commercial Terms</h6>
                <ComingSoonCommercialTermsTable data={quotes?.leftText} />
            </div>
        );
    };

    return (
        <div className="my-engagement">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <h6>
                            Ownership Representative:{' '}
                            <span>
                                {currentEngagement.engagementProcess.userName}
                            </span>
                        </h6>
                        <h6>
                            Level of Service:{' '}
                            <span>
                                {
                                    LevelsOfService.find(
                                        (l) =>
                                            l.id ===
                                            currentEngagement.levelOfService
                                    )?.name
                                }
                            </span>
                        </h6>
                        <h6>
                            Engagement Start Date: <span>{formattedDate}</span>
                        </h6>
                        <h6>
                            FTE DVMs, at engagement start:{' '}
                            <span>
                                {currentEngagement.engagementProcess.startFteDvm}
                            </span>
                            {props.isAdminView && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ml-2"
                                    onClick={() => setIsEditValuesModalVisible(true)}
                                    style={{ verticalAlign: 'baseline' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                            )}
                        </h6>
                        <h6>
                            LTM Revenue, at engagement start:{' '}
                            <span>
                                ${currentEngagement.engagementProcess.startLtmRevenue.toLocaleString()}
                            </span>
                            {props.isAdminView && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ml-2"
                                    onClick={() => setIsEditValuesModalVisible(true)}
                                    style={{ verticalAlign: 'baseline' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                            )}
                        </h6>
                        <div className="engagement-status-row">
                            <h6>
                                Engagement Status:{' '}
                                <span>
                                    {
                                        EngagementProcessStatuses.find(
                                            (x) =>
                                                x.id ===
                                                currentEngagement
                                                    .engagementProcess.status
                                        ).name
                                    }
                                </span>
                            </h6>
                            <NotificationFlag
                                eventCode={notificationFlagCode}
                                includePrefix={true}></NotificationFlag>
                        </div>
                        <h6>Practices:</h6>
                        {getPracticesTable()}
                        {getButtonsSection()}
                        {getCommercialTermsSection()}
                    </div>
                </div>
            </div>
            {isSuspendEngagementPopupVisible && (
                <SuspendEngagementPopup
                    engagementId={
                        currentEngagement.engagementProcess.timelineId
                    }
                    fteDvmNumber={currentEngagement.engagementProcess.startFteDvm || 0}
                    levelOfService={currentEngagement.levelOfService}
                    isVisible={isSuspendEngagementPopupVisible}
                    setIsVisible={setIsSuspendEngagementPopupVisible}
                    fetchData={props.fetchData}
                />
            )}
            {isTerminateEngagementPopupVisible && (
                <TerminateEngagementPopup
                    engagementId={
                        currentEngagement.engagementProcess.timelineId
                    }
                    levelOfService={currentEngagement.levelOfService}
                    isVisible={isTerminateEngagementPopupVisible}
                    setIsVisible={setIsTerminateEngagementPopupVisible}
                    fetchData={props.fetchData}
                />
            )}
            {isResumeEngagementPopupVisible && (
                <ConfirmationModal
                    header={'Confirm Resuming Engagement'}
                    isVisible={isResumeEngagementPopupVisible}
                    setIsVisible={setIsResumeEngagementPopupVisible}
                    confirmationText="Are you sure you want to resume the engagement?"
                    params={''}
                    onConfirm={() => {
                        if (!isResumeEngagementInProcess) {
                            resumeEngagement();
                        }
                    }}
                    nextButtonText="Resume"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isResumeEngagementInProcess}
                />
            )}
            {props.isAdminView && isEditValuesModalVisible && (
                <EditEngagementValuesModal
                    engagementId={currentEngagement.engagementProcess.timelineId}
                    currentFteDvm={currentEngagement.engagementProcess.startFteDvm}
                    currentLtmRevenue={currentEngagement.engagementProcess.startLtmRevenue}
                    isVisible={isEditValuesModalVisible}
                    setIsVisible={setIsEditValuesModalVisible}
                    onSave={handleSaveEngagementValues}
                    isSaving={isSavingValues}
                />
            )}
        </div>
    );
};

export default MyEngagement;